import type {
  TagFilter,
  CriteriaFilter,
  DateRangeFilter,
  ScoreFilter,
} from "../types";

/**
 * The filter context sent in the body of POST /api/v1/metadata/values.
 *
 * Field names mirror the backend `Filters` model
 * (align/core/app/evaluations/filters.py): tag filters live under `filters`,
 * alongside `criteria_filter`, `date_range`, and `score_filter`. The backend's
 * `page` / `page_size` / `exclude_fields` are irrelevant here and omitted.
 */
export interface MetadataFilterContext {
  filters?: TagFilter[];
  criteria_filter?: { values: string[] };
  date_range?: { start_date?: string; end_date?: string };
  score_filter?: { min_score?: number; max_score?: number };
}

interface FilterState {
  filters: TagFilter[];
  criteriaFilter?: CriteriaFilter;
  dateRangeFilter?: DateRangeFilter;
  scoreFilter?: ScoreFilter;
}

interface BuildOptions {
  /** Tag key to strip from the context (a tag dropdown must not narrow itself). */
  excludeTagKey?: string;
  /** Whether to include the criteria filter (false for the criteria dropdown's own self-strip). */
  includeCriteria?: boolean;
}

/**
 * Serialise the active insights filter state into the backend `Filters` shape,
 * applying the requested self-strip. Returns `undefined` when nothing is active,
 * so the request body stays minimal and the unfiltered (backwards-compatible)
 * path is used.
 */
function buildFilterContext(
  { filters, criteriaFilter, dateRangeFilter, scoreFilter }: FilterState,
  { excludeTagKey, includeCriteria = true }: BuildOptions = {},
): MetadataFilterContext | undefined {
  const ctx: MetadataFilterContext = {};

  const tagFilters = filters.filter(
    (f) => f.key !== excludeTagKey && f.values.length > 0,
  );
  if (tagFilters.length > 0) {
    ctx.filters = tagFilters;
  }

  if (includeCriteria && criteriaFilter && criteriaFilter.values.length > 0) {
    ctx.criteria_filter = { values: criteriaFilter.values };
  }

  if (dateRangeFilter && (dateRangeFilter.start_date || dateRangeFilter.end_date)) {
    ctx.date_range = {
      start_date: dateRangeFilter.start_date,
      end_date: dateRangeFilter.end_date,
    };
  }

  if (
    scoreFilter &&
    (scoreFilter.min_score != null || scoreFilter.max_score != null)
  ) {
    ctx.score_filter = {
      min_score: scoreFilter.min_score,
      max_score: scoreFilter.max_score,
    };
  }

  return Object.keys(ctx).length > 0 ? ctx : undefined;
}

/**
 * Filter context for one tag-value dropdown: every active filter except the
 * dropdown's own tag key, so a tag never self-filters its own options (faceted
 * search — see design doc 002 ticket 2c).
 */
export function buildMetadataFilterContext(
  selfKey: string,
  state: FilterState,
): MetadataFilterContext | undefined {
  return buildFilterContext(state, { excludeTagKey: selfKey, includeCriteria: true });
}

/**
 * Filter context for the criteria dropdown: every active tag/date/score filter,
 * but never the criteria filter itself (the criteria list must not self-filter).
 */
export function buildCriteriaFilterContext(
  state: FilterState,
): MetadataFilterContext | undefined {
  return buildFilterContext(state, { includeCriteria: false });
}
